/**
 * Content Plan Service
 *
 * Handles content volume calculations, plan CRUD operations, and freshness scoring
 * for the Bulk Content Planner feature.
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { scheduleOrganicPosts } from './bulkSchedulingService';

const CONTENT_PLANS_COLLECTION = 'contentPlans';
const CONTENT_BRIEFS_COLLECTION = 'contentBriefs';

// ============================================================================
// PLATFORM FRESHNESS RULES
// ============================================================================

export const PLATFORM_FRESHNESS_RULES = {
    instagram: {
        feed: { optimalDays: 14, maxDays: 21, refreshPriority: 'visual' },
        stories: { optimalDays: 7, maxDays: 14, refreshPriority: 'copy' },
        reels: { optimalDays: 7, maxDays: 10, refreshPriority: 'hook' }
    },
    facebook: {
        feed: { optimalDays: 21, maxDays: 30, refreshPriority: 'copy' },
        ads: { optimalDays: 14, maxDays: 21, refreshPriority: 'visual' }
    },
    tiktok: {
        feed: { optimalDays: 7, maxDays: 10, refreshPriority: 'hook' }
    },
    linkedin: {
        feed: { optimalDays: 30, maxDays: 45, refreshPriority: 'copy' }
    },
    twitter: {
        feed: { optimalDays: 3, maxDays: 7, refreshPriority: 'copy' }
    },
    google: {
        ads: { optimalDays: 21, maxDays: 30, refreshPriority: 'visual' }
    }
};

// ============================================================================
// FUNNEL STAGE FRESHNESS MULTIPLIERS
// ============================================================================

export const FUNNEL_FRESHNESS_RULES = {
    TOFU: {
        multiplier: 1.2,
        description: 'Awareness content can run longer'
    },
    MOFU: {
        multiplier: 1.0,
        description: 'Standard freshness cycle'
    },
    BOFU: {
        multiplier: 0.7,
        description: 'Conversion content needs faster refresh for urgency'
    }
};

// ============================================================================
// BASE VOLUME RECOMMENDATIONS
// ============================================================================

const BASE_ORGANIC_VOLUME = {
    instagram: { postsPerMonth: 20, postsPerWeek: 5 },
    facebook: { postsPerMonth: 12, postsPerWeek: 3 },
    tiktok: { postsPerMonth: 16, postsPerWeek: 4 },
    linkedin: { postsPerMonth: 8, postsPerWeek: 2 },
    twitter: { postsPerMonth: 30, postsPerWeek: 7 }
};

const PAID_VOLUME_DEFAULTS = {
    creativesPerCampaign: 3,
    refreshCycleDays: 14,
    abTestMultiplier: 1.5
};

const FUNNEL_DISTRIBUTION = {
    TOFU: 0.40,
    MOFU: 0.35,
    BOFU: 0.25
};

const DEFAULT_CONTENT_TYPE_DISTRIBUTION = {
    image: 0.50,
    video: 0.30,
    carousel: 0.20
};

// ============================================================================
// DEFAULT PLAN STRUCTURE
// ============================================================================

export const DEFAULT_CONTENT_PLAN = {
    name: '',
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
    status: 'draft', // draft | active | completed

    config: {
        includeOrganic: true,
        includePaid: true,
        targetPlatforms: ['instagram', 'facebook'],
        volumeOverride: null,
        contentTypeDistribution: {
            image: 50,
            video: 30,
            carousel: 20
        }
    },

    recommendations: {
        totalPosts: 0,
        organicPosts: 0,
        paidCreatives: 0,
        byPillar: {},
        byFunnelStage: {
            TOFU: { count: 0, percentage: 40 },
            MOFU: { count: 0, percentage: 35 },
            BOFU: { count: 0, percentage: 25 }
        },
        byContentType: {
            image: 0,
            video: 0,
            carousel: 0
        }
    },

    freshnessRules: {
        platformRules: PLATFORM_FRESHNESS_RULES,
        funnelStageRules: FUNNEL_FRESHNESS_RULES,
        performanceRules: {
            ctrDropThreshold: 0.3,
            frequencyCapThreshold: 3,
            engagementDropThreshold: 0.4
        }
    }
};

// ============================================================================
// DEFAULT BRIEF STRUCTURE
// ============================================================================

export const DEFAULT_CONTENT_BRIEF = {
    sequence: 0,
    status: 'draft', // draft | ai_generated | refined | approved | generated | scheduled

    contentType: 'organic', // organic | paid
    outputType: 'image', // image | video | carousel
    pillarId: null,
    pillarName: '',
    pillarColor: '#6366F1',
    funnelStage: 'TOFU',

    brief: {
        product: '',
        productId: null,          // Reference to product in products collection
        productPageUrl: '',       // Landing page URL for paid content CTAs
        targetAudience: '',
        objective: '',
        marketingAngle: '',
        desiredEmotion: '',
        selectedHeadline: '',
        cta: '',
        tone: '',
        visualStyle: '',
        imageType: '',
        sceneDescription: '',
        videoSettings: null,
        // Post-ready content fields
        caption: '',      // Social media caption ready to post
        hashtags: []      // Array of hashtags (without # symbol)
    },

    abTest: null, // { isAbTest, testElement, variants: [] }

    userNotes: '',
    isUserRefined: false,
    refinementHistory: [], // Array of { instruction, timestamp } to track user refinements

    suggestedDate: null,
    scheduledDate: null,
    platforms: [],

    freshness: {
        createdAt: null,
        lastRefreshedAt: null,
        refreshCount: 0,
        parentBriefId: null,
        freshnessScore: 100
    },

    generatedAssets: {
        libraryItemId: null,
        imageUrl: null,
        thumbnailUrl: null,
        aspectRatio: null,
        variantCount: 0,
        // Size variants for multi-platform support
        sizeVariants: [] // Array of { aspectRatio, platforms, formatName, imageUrl, thumbnailUrl }
    }
};

// ============================================================================
// VOLUME CALCULATION
// ============================================================================

/**
 * Calculate recommended content volume based on business goals and configuration
 */
export async function calculateRecommendedVolume(brandId, config, pillars = [], businessGoals = {}) {
    const { includeOrganic, includePaid, targetPlatforms, volumeOverride, daysInRange = 30, contentTypeDistribution } = config;

    // Scale factor: how many days vs a full month (30 days)
    const scaleFactor = daysInRange / 30;

    // Calculate base organic volume (scaled by date range)
    let organicPosts = 0;
    if (includeOrganic) {
        targetPlatforms.forEach(platform => {
            const platformVolume = BASE_ORGANIC_VOLUME[platform];
            if (platformVolume) {
                // Scale posts per month based on actual days selected
                organicPosts += Math.ceil(platformVolume.postsPerMonth * scaleFactor);
            }
        });
    }

    // Calculate paid creatives based on business goals (scaled by date range)
    let paidCreatives = 0;
    if (includePaid) {
        // Estimate campaigns based on revenue target
        const revenueTarget = businessGoals.monthlyRevenueTarget || 50000;
        const estimatedCampaigns = Math.max(3, Math.ceil(revenueTarget / 25000)); // 1 campaign per $25k target

        paidCreatives = Math.ceil(estimatedCampaigns * PAID_VOLUME_DEFAULTS.creativesPerCampaign * scaleFactor);

        // Add A/B test multiplier
        paidCreatives = Math.ceil(paidCreatives * PAID_VOLUME_DEFAULTS.abTestMultiplier);
    }

    // Apply volume override if specified (scaled by date range)
    let totalPosts = volumeOverride
        ? Math.ceil(volumeOverride * scaleFactor)
        : (organicPosts + paidCreatives);

    // Scale based on revenue goals
    if (!volumeOverride && businessGoals.monthlyRevenueTarget) {
        const revenueFactor = Math.min(2, Math.max(0.5, businessGoals.monthlyRevenueTarget / 50000));
        totalPosts = Math.ceil(totalPosts * revenueFactor);
        organicPosts = Math.ceil(organicPosts * revenueFactor);
        paidCreatives = Math.ceil(paidCreatives * revenueFactor);
    }

    // Ensure minimum of 1 post if any content type is selected
    if (totalPosts === 0 && (includeOrganic || includePaid)) {
        totalPosts = 1;
    }

    // Calculate distribution by funnel stage
    const byFunnelStage = {
        TOFU: {
            count: Math.ceil(totalPosts * FUNNEL_DISTRIBUTION.TOFU),
            percentage: Math.round(FUNNEL_DISTRIBUTION.TOFU * 100)
        },
        MOFU: {
            count: Math.ceil(totalPosts * FUNNEL_DISTRIBUTION.MOFU),
            percentage: Math.round(FUNNEL_DISTRIBUTION.MOFU * 100)
        },
        BOFU: {
            count: Math.ceil(totalPosts * FUNNEL_DISTRIBUTION.BOFU),
            percentage: Math.round(FUNNEL_DISTRIBUTION.BOFU * 100)
        }
    };

    // Calculate distribution by pillar
    // Use Math.round instead of Math.ceil to avoid over-allocation
    const byPillar = {};
    if (pillars.length > 0) {
        const activePillars = pillars.filter(p => !p.isArchived);
        const totalWeight = activePillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 1), 0);

        // First pass: calculate raw counts with Math.round
        let allocatedTotal = 0;
        const pillarCounts = activePillars.map(pillar => {
            const weight = pillar.frequency?.currentWeight || 1;
            const rawCount = totalPosts * (weight / totalWeight);
            const count = Math.round(rawCount);
            allocatedTotal += count;
            return { pillar, weight, count };
        });

        // Adjust to match totalPosts exactly (handle rounding drift)
        let diff = totalPosts - allocatedTotal;
        if (diff !== 0) {
            // Sort by weight descending to give extra/remove from highest weight pillars
            pillarCounts.sort((a, b) => b.weight - a.weight);
            for (let i = 0; diff !== 0 && i < pillarCounts.length; i++) {
                if (diff > 0) {
                    pillarCounts[i].count++;
                    diff--;
                } else if (diff < 0 && pillarCounts[i].count > 0) {
                    pillarCounts[i].count--;
                    diff++;
                }
            }
        }

        pillarCounts.forEach(({ pillar, weight, count }) => {
            const percentage = Math.round((weight / totalWeight) * 100);

            byPillar[pillar.id] = {
                pillarId: pillar.id,
                pillarName: pillar.name,
                pillarColor: pillar.color,
                recommended: count,
                percentage
            };
        });
    }

    // Calculate content type distribution (use custom if provided, otherwise default)
    const typeDistribution = contentTypeDistribution || DEFAULT_CONTENT_TYPE_DISTRIBUTION;
    // Convert from percentages (0-100) to decimals (0-1) if needed
    const imageRatio = typeDistribution.image > 1 ? typeDistribution.image / 100 : typeDistribution.image;
    const videoRatio = typeDistribution.video > 1 ? typeDistribution.video / 100 : typeDistribution.video;
    const carouselRatio = typeDistribution.carousel > 1 ? typeDistribution.carousel / 100 : typeDistribution.carousel;

    const byContentType = {
        image: Math.ceil(totalPosts * imageRatio),
        video: Math.ceil(totalPosts * videoRatio),
        carousel: Math.ceil(totalPosts * carouselRatio)
    };

    return {
        totalPosts,
        organicPosts,
        paidCreatives,
        byPillar,
        byFunnelStage,
        byContentType
    };
}

// ============================================================================
// PLAN CRUD OPERATIONS
// ============================================================================

/**
 * Create a new content plan
 */
export async function createContentPlan(brandId, userId, planData) {
    if (!brandId) throw new Error('brandId is required');
    if (!userId) throw new Error('userId is required');

    const { month, year } = planData;

    const plan = {
        ...DEFAULT_CONTENT_PLAN,
        ...planData,
        brandId,
        userId,
        month,
        year,
        name: planData.name || `${getMonthName(month)} ${year} Content Plan`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };

    console.log('Creating content plan with userId:', userId, 'brandId:', brandId);

    try {
        const docRef = await addDoc(collection(db, CONTENT_PLANS_COLLECTION), plan);
        console.log('Content plan created successfully:', docRef.id);
        return { id: docRef.id, ...plan };
    } catch (error) {
        console.error('Failed to create content plan:', error);
        throw error;
    }
}

/**
 * Get a content plan by ID
 */
export async function getContentPlan(planId) {
    const docRef = doc(db, CONTENT_PLANS_COLLECTION, planId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get all content plans for a brand
 */
export async function getContentPlansByBrand(brandId) {
    const q = query(
        collection(db, CONTENT_PLANS_COLLECTION),
        where('brandId', '==', brandId),
        orderBy('year', 'desc'),
        orderBy('month', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get incomplete content plans for a brand (not yet scheduled)
 * Returns plans with status 'draft' or 'active' that can be resumed
 */
export async function getIncompletePlans(brandId, userId) {
    if (!brandId) return [];
    if (!userId) return [];

    // Query plans that are not yet scheduled/completed
    const q = query(
        collection(db, CONTENT_PLANS_COLLECTION),
        where('brandId', '==', brandId),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const plans = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filter to only incomplete plans (not 'scheduled' or 'completed') and not archived
    const incompletePlans = plans.filter(plan =>
        plan.status !== 'scheduled' && plan.status !== 'completed' && !plan.isArchived
    );

    // For each incomplete plan, get brief counts to show progress
    const plansWithProgress = await Promise.all(
        incompletePlans.map(async (plan) => {
            try {
                const briefsQuery = query(
                    collection(db, CONTENT_BRIEFS_COLLECTION),
                    where('planId', '==', plan.id),
                    where('userId', '==', userId)
                );
                const briefsSnapshot = await getDocs(briefsQuery);
                const briefs = briefsSnapshot.docs.map(doc => doc.data());

                const totalBriefs = briefs.length;
                const approvedBriefs = briefs.filter(b =>
                    b.status === 'approved' || b.status === 'generated' || b.status === 'scheduled'
                ).length;
                const generatedBriefs = briefs.filter(b =>
                    b.status === 'generated' || b.status === 'scheduled'
                ).length;

                // Determine resume step
                let resumeStep = 'overview';
                if (totalBriefs === 0) {
                    resumeStep = plan.id ? 'generate' : 'overview';
                } else if (generatedBriefs > 0) {
                    resumeStep = 'schedule';
                } else if (approvedBriefs > 0) {
                    resumeStep = 'creatives';
                } else {
                    resumeStep = 'review';
                }

                return {
                    ...plan,
                    progress: {
                        totalBriefs,
                        approvedBriefs,
                        generatedBriefs,
                        resumeStep
                    }
                };
            } catch (error) {
                console.error(`Failed to get briefs for plan ${plan.id}:`, error);
                return {
                    ...plan,
                    progress: {
                        totalBriefs: 0,
                        approvedBriefs: 0,
                        generatedBriefs: 0,
                        resumeStep: 'overview'
                    }
                };
            }
        })
    );

    return plansWithProgress;
}

/**
 * Get a content plan with all its briefs
 * @param {string} planId
 * @param {string} [userId] - Required for security rule compliance when reading briefs
 */
export async function getContentPlanWithBriefs(planId, userId = null) {
    const plan = await getContentPlan(planId);
    if (!plan) return null;

    // Security rules require userId filter on contentBriefs reads
    const constraints = [
        where('planId', '==', planId),
        orderBy('sequence', 'asc')
    ];
    if (userId) {
        constraints.unshift(where('userId', '==', userId));
    }

    const briefsQuery = query(
        collection(db, CONTENT_BRIEFS_COLLECTION),
        ...constraints
    );

    const briefsSnapshot = await getDocs(briefsQuery);
    const briefs = briefsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return { plan, briefs };
}

/**
 * Update a content plan
 */
export async function updateContentPlan(planId, updates) {
    const docRef = doc(db, CONTENT_PLANS_COLLECTION, planId);
    await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
    });
    return getContentPlan(planId);
}

/**
 * Archive a content plan (soft delete - keeps data but hides from active view)
 * @param {string} planId - The plan ID to archive
 * @param {string} userId - User ID for security rule compliance
 */
export async function archiveContentPlan(planId, userId) {
    if (!planId) throw new Error('planId is required');
    if (!userId) throw new Error('userId is required');

    const planRef = doc(db, CONTENT_PLANS_COLLECTION, planId);
    const planSnap = await getDoc(planRef);

    if (!planSnap.exists()) {
        throw new Error('Plan not found');
    }

    const plan = planSnap.data();
    if (plan.userId !== userId) {
        throw new Error('You do not have permission to archive this plan');
    }

    await updateDoc(planRef, {
        isArchived: true,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });

    console.log(`Content plan ${planId} archived successfully`);
    return { id: planId, ...plan, isArchived: true };
}

/**
 * Restore an archived content plan
 * @param {string} planId - The plan ID to restore
 * @param {string} userId - User ID for security rule compliance
 */
export async function restoreContentPlan(planId, userId) {
    if (!planId) throw new Error('planId is required');
    if (!userId) throw new Error('userId is required');

    const planRef = doc(db, CONTENT_PLANS_COLLECTION, planId);
    const planSnap = await getDoc(planRef);

    if (!planSnap.exists()) {
        throw new Error('Plan not found');
    }

    const plan = planSnap.data();
    if (plan.userId !== userId) {
        throw new Error('You do not have permission to restore this plan');
    }

    await updateDoc(planRef, {
        isArchived: false,
        archivedAt: null,
        updatedAt: serverTimestamp()
    });

    console.log(`Content plan ${planId} restored successfully`);
    return { id: planId, ...plan, isArchived: false };
}

/**
 * Get archived content plans for a brand
 * @param {string} brandId - The brand ID
 * @param {string} userId - The user ID
 */
export async function getArchivedPlans(brandId, userId) {
    if (!brandId || !userId) return [];

    const q = query(
        collection(db, CONTENT_PLANS_COLLECTION),
        where('brandId', '==', brandId),
        where('userId', '==', userId),
        where('isArchived', '==', true),
        orderBy('archivedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get completed content plans for a brand (status = 'scheduled' or 'completed')
 * @param {string} brandId - The brand ID
 * @param {string} userId - The user ID
 */
export async function getCompletedPlans(brandId, userId) {
    if (!brandId || !userId) return [];

    // Query for scheduled plans (these are "completed" from the planning perspective)
    const q = query(
        collection(db, CONTENT_PLANS_COLLECTION),
        where('brandId', '==', brandId),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const plans = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filter for completed/scheduled plans that are not archived
    return plans.filter(plan =>
        (plan.status === 'scheduled' || plan.status === 'completed') &&
        !plan.isArchived
    );
}

/**
 * Check if a plan has any scheduled posts that haven't been published yet
 * @param {string} planId - The plan ID to check
 * @returns {Promise<{hasScheduledPosts: boolean, scheduledCount: number, scheduledPosts: Array}>}
 */
export async function checkPlanScheduledPosts(planId) {
    if (!planId) return { hasScheduledPosts: false, scheduledCount: 0, scheduledPosts: [] };

    // Query posts collection for scheduled posts from this plan
    const postsQuery = query(
        collection(db, 'posts'),
        where('planId', '==', planId),
        where('status', '==', 'scheduled')
    );

    const snapshot = await getDocs(postsQuery);
    const scheduledPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));

    return {
        hasScheduledPosts: scheduledPosts.length > 0,
        scheduledCount: scheduledPosts.length,
        scheduledPosts
    };
}

/**
 * Get library items associated with a content plan
 * @param {string} planId - The plan ID
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<{items: Array, count: number}>}
 */
export async function getPlanLibraryItems(planId, userId, brandId) {
    if (!planId || !userId || !brandId) {
        return { items: [], count: 0 };
    }

    // Library items have planId in their metadata
    const libraryQuery = query(
        collection(db, 'libraryItems'),
        where('userId', '==', userId),
        where('brandId', '==', brandId)
    );

    const snapshot = await getDocs(libraryQuery);
    const allItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));

    // Filter items that belong to this plan (planId stored in metadata)
    const planItems = allItems.filter(item =>
        item.metadata?.planId === planId ||
        item.planId === planId
    );

    return {
        items: planItems,
        count: planItems.length
    };
}

/**
 * Delete library items associated with a content plan
 * @param {string} planId - The plan ID
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<{deletedCount: number}>}
 */
export async function deletePlanLibraryItems(planId, userId, brandId) {
    const { items } = await getPlanLibraryItems(planId, userId, brandId);

    if (items.length === 0) {
        return { deletedCount: 0 };
    }

    console.log(`Deleting ${items.length} library items for plan ${planId}`);

    const deletePromises = items.map(item =>
        deleteDoc(doc(db, 'libraryItems', item.id))
    );

    await Promise.all(deletePromises);
    console.log(`Deleted ${items.length} library items`);

    return { deletedCount: items.length };
}

/**
 * Delete a content plan and all its briefs
 * @param {string} planId - The plan ID to delete
 * @param {string} [userId] - User ID for security rule compliance when deleting briefs
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.deleteLibraryItems] - Whether to also delete associated library items
 * @param {string} [options.brandId] - Required if deleteLibraryItems is true
 */
export async function deleteContentPlan(planId, userId = null, options = {}) {
    if (!planId) throw new Error('planId is required');
    if (!userId) throw new Error('userId is required for security rule compliance');

    console.log(`Deleting content plan ${planId} for user ${userId}`);

    // Check for scheduled posts first
    const { hasScheduledPosts, scheduledCount } = await checkPlanScheduledPosts(planId);
    if (hasScheduledPosts) {
        throw new Error(`Cannot delete plan: ${scheduledCount} post(s) are still scheduled. Please cancel or publish them first.`);
    }

    // Delete library items if requested
    if (options.deleteLibraryItems && options.brandId) {
        await deletePlanLibraryItems(planId, userId, options.brandId);
    }

    // Delete all briefs first
    // Security rules require userId filter on contentBriefs reads/deletes
    const briefsQuery = query(
        collection(db, CONTENT_BRIEFS_COLLECTION),
        where('userId', '==', userId),
        where('planId', '==', planId)
    );

    try {
        const briefsSnapshot = await getDocs(briefsQuery);
        console.log(`Found ${briefsSnapshot.docs.length} briefs to delete`);

        if (briefsSnapshot.docs.length > 0) {
            const deletePromises = briefsSnapshot.docs.map(briefDoc => deleteDoc(briefDoc.ref));
            await Promise.all(deletePromises);
            console.log('Briefs deleted successfully');
        }
    } catch (briefError) {
        console.error('Error deleting briefs:', briefError);
        throw new Error(`Failed to delete briefs: ${briefError.message}`);
    }

    // Delete the plan
    try {
        const planRef = doc(db, CONTENT_PLANS_COLLECTION, planId);
        await deleteDoc(planRef);
        console.log('Content plan deleted successfully');
    } catch (planError) {
        console.error('Error deleting plan:', planError);
        throw planError;
    }
}

// ============================================================================
// BRIEF CRUD OPERATIONS
// ============================================================================

/**
 * Create a content brief
 */
export async function createContentBrief(planId, brandId, userId, briefData) {
    if (!planId) throw new Error('planId is required');
    if (!brandId) throw new Error('brandId is required');
    if (!userId) throw new Error('userId is required');

    // Extract nested brief content fields from briefData
    // The AI generates these at the top level, but we need them nested in 'brief'
    const briefContentFields = [
        'product', 'productId', 'productPageUrl', 'targetAudience', 'objective', 'marketingAngle',
        'desiredEmotion', 'selectedHeadline', 'cta', 'tone',
        'visualStyle', 'imageType', 'sceneDescription', 'hookDescription',
        'headline', 'videoSettings',
        // Post-ready content fields
        'caption', 'hashtags',
        // Carousel-specific fields
        'carouselCards', 'storyArcType', 'storyNarrative'
    ];

    // Build the nested brief object
    const nestedBrief = { ...DEFAULT_CONTENT_BRIEF.brief };

    // If briefData already has a nested 'brief' object, merge it first
    if (briefData.brief && typeof briefData.brief === 'object') {
        Object.assign(nestedBrief, briefData.brief);
    }

    // Extract top-level fields AFTER Object.assign so they always take priority
    briefContentFields.forEach(field => {
        if (briefData[field] !== undefined) {
            nestedBrief[field] = briefData[field];
        }
    });

    // Build the final brief document
    const brief = {
        ...DEFAULT_CONTENT_BRIEF,
        planId,
        brandId,
        userId,
        sequence: briefData.sequence || 0,
        status: briefData.status || 'ai_generated',
        contentType: briefData.contentType || 'organic',
        outputType: briefData.outputType || 'image',
        pillarId: briefData.pillarId || null,
        pillarName: briefData.pillarName || '',
        pillarColor: briefData.pillarColor || '#6366F1',
        funnelStage: briefData.funnelStage || 'TOFU',
        abTest: briefData.abTest || null,
        userNotes: briefData.userNotes || '',
        isUserRefined: briefData.isUserRefined || false,
        refinementHistory: briefData.refinementHistory || [],
        suggestedDate: briefData.suggestedDate || null,
        scheduledDate: briefData.scheduledDate || null,
        platforms: briefData.platforms || [],
        brief: nestedBrief,
        freshness: {
            ...DEFAULT_CONTENT_BRIEF.freshness,
            createdAt: serverTimestamp()
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };

    try {
        const docRef = await addDoc(collection(db, CONTENT_BRIEFS_COLLECTION), brief);
        return { id: docRef.id, ...brief };
    } catch (error) {
        console.error('Failed to create content brief:', error);
        throw error;
    }
}

/**
 * Create multiple briefs in batch
 */
export async function createContentBriefsBatch(planId, brandId, userId, briefsData) {
    if (!planId) throw new Error('planId is required');
    if (!brandId) throw new Error('brandId is required');
    if (!userId) throw new Error('userId is required');
    if (!briefsData || briefsData.length === 0) throw new Error('briefsData is required');

    console.log(`Creating ${briefsData.length} briefs for plan ${planId}`);
    const briefs = [];

    for (let i = 0; i < briefsData.length; i++) {
        try {
            const brief = await createContentBrief(planId, brandId, userId, {
                ...briefsData[i],
                sequence: i + 1
            });
            briefs.push(brief);
        } catch (error) {
            console.error(`Failed to create brief ${i + 1}:`, error);
            throw error;
        }
    }

    console.log(`Successfully created ${briefs.length} briefs`);
    return briefs;
}

/**
 * Get a content brief by ID
 */
export async function getContentBrief(briefId) {
    const docRef = doc(db, CONTENT_BRIEFS_COLLECTION, briefId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get all briefs for a plan
 */
export async function getBriefsByPlan(planId) {
    const q = query(
        collection(db, CONTENT_BRIEFS_COLLECTION),
        where('planId', '==', planId),
        orderBy('sequence', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get approved briefs for a plan
 */
export async function getApprovedBriefs(planId) {
    const q = query(
        collection(db, CONTENT_BRIEFS_COLLECTION),
        where('planId', '==', planId),
        where('status', '==', 'approved')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Update a content brief
 */
export async function updateContentBrief(briefId, updates) {
    const docRef = doc(db, CONTENT_BRIEFS_COLLECTION, briefId);
    await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
    });
    return getContentBrief(briefId);
}

/**
 * Delete a content brief
 */
export async function deleteContentBrief(briefId) {
    const docRef = doc(db, CONTENT_BRIEFS_COLLECTION, briefId);
    await deleteDoc(docRef);
}

// ============================================================================
// FRESHNESS SCORING
// ============================================================================

/**
 * Calculate days since a date
 */
function daysSince(date) {
    if (!date) return 0;
    const timestamp = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - timestamp);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate freshness score for a piece of content
 */
export function calculateFreshnessScore(content, platform, funnelStage, performance = null) {
    // Get platform rules (default to feed type)
    const platformRules = PLATFORM_FRESHNESS_RULES[platform]?.feed ||
                          PLATFORM_FRESHNESS_RULES[platform]?.ads ||
                          { optimalDays: 14, maxDays: 21, refreshPriority: 'visual' };

    // Get funnel multiplier
    const funnelMultiplier = FUNNEL_FRESHNESS_RULES[funnelStage]?.multiplier || 1.0;

    // Calculate effective max days
    const effectiveMaxDays = platformRules.maxDays * funnelMultiplier;

    // Get days active
    const publishDate = content.publishedAt || content.scheduledDate || content.createdAt;
    const daysActive = daysSince(publishDate);

    // Base freshness score (100 = fresh, 0 = expired)
    let score = Math.max(0, 100 - (daysActive / effectiveMaxDays) * 100);

    // Apply performance-based penalties
    if (performance) {
        // CTR trend penalty
        if (performance.initialCTR && performance.currentCTR) {
            const ctrTrend = performance.currentCTR / performance.initialCTR;
            if (ctrTrend < 0.7) score -= 15; // 30% CTR drop
            if (ctrTrend < 0.5) score -= 25; // 50% CTR drop
        }

        // Frequency cap penalty
        const avgFrequency = performance.averageFrequency || 1;
        if (avgFrequency > 3) {
            const frequencyPenalty = Math.min(20, (avgFrequency - 2) * 5);
            score -= frequencyPenalty;
        }

        // Engagement trend penalty
        if (performance.engagementTrend === 'declining') {
            score -= 10;
        }
    }

    // Ensure score is within bounds
    score = Math.max(0, Math.min(100, Math.round(score)));

    // Determine status
    let status;
    if (score >= 70) status = 'fresh';
    else if (score >= 50) status = 'aging';
    else if (score >= 30) status = 'stale';
    else status = 'expired';

    // Generate recommendation
    let recommendation;
    if (score >= 70) {
        recommendation = 'continue';
    } else if (score >= 50) {
        recommendation = `refresh_${platformRules.refreshPriority}`;
    } else if (score >= 30) {
        recommendation = 'refresh_all';
    } else {
        recommendation = 'retire';
    }

    return {
        score,
        status,
        daysActive,
        daysUntilRefresh: Math.max(0, Math.round(effectiveMaxDays - daysActive)),
        recommendation,
        factors: {
            platformMaxDays: platformRules.maxDays,
            funnelMultiplier,
            effectiveMaxDays: Math.round(effectiveMaxDays),
            refreshPriority: platformRules.refreshPriority
        }
    };
}

/**
 * Get all content needing refresh for a brand
 */
export async function getRefreshCandidates(brandId, platforms = []) {
    // This would query scheduled/published content and calculate freshness
    // For now, return empty array - will be enhanced when integrated with scheduler
    const candidates = [];

    // TODO: Query posts collection, calculate freshness for each, return sorted by urgency

    return candidates;
}

/**
 * Get freshness summary for a brand across all platforms
 */
export async function getFreshnessSummary(brandId) {
    // This would aggregate freshness data across all platforms
    return {
        fresh: 0,
        aging: 0,
        stale: 0,
        expired: 0,
        byPlatform: {},
        refreshCandidates: []
    };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getMonthName(monthIndex) {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex] || 'Unknown';
}

/**
 * Distribute total count across funnel stages
 */
export function distributeBriefsByFunnelStage(totalCount, distribution = FUNNEL_DISTRIBUTION) {
    const result = {
        TOFU: Math.ceil(totalCount * distribution.TOFU),
        MOFU: Math.ceil(totalCount * distribution.MOFU),
        BOFU: totalCount - Math.ceil(totalCount * distribution.TOFU) - Math.ceil(totalCount * distribution.MOFU)
    };

    // Ensure BOFU is at least 1 if we have content
    if (totalCount > 0 && result.BOFU < 1) {
        result.BOFU = 1;
        result.TOFU = Math.max(1, result.TOFU - 1);
    }

    return result;
}

/**
 * Distribute count across pillars based on weights
 */
export function distributeBriefsByPillar(totalCount, pillars) {
    if (!pillars || pillars.length === 0) {
        return {};
    }

    const activePillars = pillars.filter(p => !p.isArchived);
    const totalWeight = activePillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 1), 0);

    const distribution = {};
    let remaining = totalCount;

    activePillars.forEach((pillar, index) => {
        const weight = pillar.frequency?.currentWeight || 1;
        const isLast = index === activePillars.length - 1;

        if (isLast) {
            distribution[pillar.id] = remaining;
        } else {
            const count = Math.ceil(totalCount * (weight / totalWeight));
            distribution[pillar.id] = count;
            remaining -= count;
        }
    });

    return distribution;
}

/**
 * Generate suggested dates for briefs across a month
 */
export function generateSuggestedDates(count, month, year, platforms = []) {
    const dates = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Calculate optimal posting frequency
    const spacing = Math.max(1, Math.floor(daysInMonth / count));

    for (let i = 0; i < count; i++) {
        const day = Math.min(daysInMonth, 1 + (i * spacing));
        const date = new Date(year, month, day);

        // Skip weekends for LinkedIn
        if (platforms.includes('linkedin') && (date.getDay() === 0 || date.getDay() === 6)) {
            date.setDate(date.getDate() + (date.getDay() === 0 ? 1 : 2));
        }

        dates.push(date.toISOString().split('T')[0]);
    }

    return dates;
}

/**
 * Schedule content briefs with their posting times
 * Updates brief documents with scheduled date/time and persists latest content revisions.
 * For organic content, also creates posts in the posts collection for actual publishing.
 *
 * @param {string} planId - The plan ID
 * @param {string} brandId - The brand ID
 * @param {string} userId - The user ID
 * @param {Array} scheduledPosts - Array of scheduled post data with briefId, dateTime, time, platforms
 * @param {Array} [latestBriefs] - Optional array of full brief objects with latest content revisions
 * @param {Object} [brand] - Optional brand object for organic post scheduling
 */
export async function scheduleContentBriefs(planId, brandId, userId, scheduledPosts, latestBriefs = null, brand = null) {
    if (!planId) throw new Error('planId is required');
    if (!brandId) throw new Error('brandId is required');
    if (!userId) throw new Error('userId is required');
    if (!scheduledPosts || scheduledPosts.length === 0) {
        throw new Error('scheduledPosts is required');
    }

    console.log(`Scheduling ${scheduledPosts.length} posts for plan ${planId}`);

    // Create a map of latest briefs for quick lookup
    const briefsMap = new Map();
    if (latestBriefs && Array.isArray(latestBriefs)) {
        latestBriefs.forEach(brief => {
            if (brief?.id) {
                briefsMap.set(brief.id, brief);
            }
        });
    }

    const results = [];

    for (const post of scheduledPosts) {
        try {
            // Build the update object with scheduling info
            const briefUpdate = {
                status: 'scheduled',
                scheduledDate: post.dateTime instanceof Date ? post.dateTime.toISOString() : post.dateTime,
                scheduledTime: post.time,
                platforms: post.platforms || [],
                scheduleQuality: post.quality || 'good'
            };

            // If we have the latest brief content, persist it to capture any revisions
            const latestBrief = briefsMap.get(post.briefId);
            if (latestBrief) {
                // Persist the latest brief content (including any user refinements)
                if (latestBrief.brief) {
                    briefUpdate.brief = latestBrief.brief;
                }
                // Also persist any user refinement tracking
                if (latestBrief.isUserRefined !== undefined) {
                    briefUpdate.isUserRefined = latestBrief.isUserRefined;
                }
                if (latestBrief.refinementHistory) {
                    briefUpdate.refinementHistory = latestBrief.refinementHistory;
                }
                if (latestBrief.userNotes !== undefined) {
                    briefUpdate.userNotes = latestBrief.userNotes;
                }
                // Persist generated asset info if present
                if (latestBrief.generatedAssets) {
                    briefUpdate.generatedAssets = latestBrief.generatedAssets;
                }
                if (latestBrief.generatedCreativeId) {
                    briefUpdate.generatedCreativeId = latestBrief.generatedCreativeId;
                }
                if (latestBrief.generatedImageUrl) {
                    briefUpdate.generatedImageUrl = latestBrief.generatedImageUrl;
                }
                if (latestBrief.generatedThumbnailUrl) {
                    briefUpdate.generatedThumbnailUrl = latestBrief.generatedThumbnailUrl;
                }
                // Persist carousel images for carousel-type content
                if (latestBrief.carouselImages) {
                    briefUpdate.carouselImages = latestBrief.carouselImages;
                }
                // Also persist outputType to ensure correct creativeType in posts
                if (latestBrief.outputType) {
                    briefUpdate.outputType = latestBrief.outputType;
                }
            }

            // Skip Firestore update for synthetic A/B variant IDs (they aren't persisted)
            if (!post.briefId.includes('_variant')) {
                await updateContentBrief(post.briefId, briefUpdate);
            }

            results.push({
                briefId: post.briefId,
                success: true,
                scheduledDate: briefUpdate.scheduledDate,
                scheduledTime: briefUpdate.scheduledTime
            });
        } catch (error) {
            console.error(`Failed to schedule brief ${post.briefId}:`, error);
            results.push({
                briefId: post.briefId,
                success: false,
                error: error.message
            });
        }
    }

    // Update the plan status
    if (planId) {
        try {
            await updateContentPlan(planId, {
                status: 'scheduled',
                scheduledCount: results.filter(r => r.success).length
            });
        } catch (error) {
            console.error('Failed to update plan status:', error);
        }
    }

    console.log(`Successfully scheduled ${results.filter(r => r.success).length} of ${scheduledPosts.length} posts`);

    // Phase 1: Create posts in the posts collection for organic content
    // This bridges the gap between bulk planner scheduling and actual publishing
    if (brand && latestBriefs && latestBriefs.length > 0) {
        try {
            // Filter for organic posts (non-paid content)
            const organicPosts = scheduledPosts.filter(sp => {
                const brief = briefsMap.get(sp.briefId);
                return brief && brief.contentType !== 'paid';
            });

            if (organicPosts.length > 0) {
                console.log(`Creating ${organicPosts.length} organic posts in posts collection...`);

                const organicResult = await scheduleOrganicPosts(
                    organicPosts,
                    latestBriefs,
                    brand,
                    userId
                );

                if (organicResult.success) {
                    console.log(`Successfully created ${organicResult.postIds.length} organic posts`);
                } else {
                    console.warn('Some organic posts failed to schedule:', organicResult.errors);
                }

                // Add organic scheduling results to the return value
                results.organicPostIds = organicResult.postIds;
                results.organicErrors = organicResult.errors;
            }
        } catch (error) {
            console.error('Failed to schedule organic posts:', error);
            // Don't fail the entire operation, just log the error
            results.organicError = error.message;
        }
    }

    return results;
}
